import { useEffect, useState } from "react";
import Campo from "../Shared/Campo";
import GdmDadosCampos from "../Shared/GdmDadosCampos";
import { obterArquivoAnexo } from "../../../utils/solicitacoes/anexoCache";
import { formatarDataHora } from "../../../utils/solicitacoes/formatters";
import { normalizarDescricao } from "../../../utils/solicitacoes/anexosStaged";
import Button from "../../../componentes/button/button";
import PdfUnicoModal from "../Shared/PdfUnico/PdfUnicoModal";

// Ordem do PDF único: contrato, documento e comprovante (só os que foram anexados). Cada item aceita nomes
// equivalentes — "Contrato Assinado" era o nome do anexo em solicitações antigas.
const ORDEM_PDF_UNICO = [["Contrato", "Contrato Assinado"], "Documento do Responsável", "Comprovante de Endereço"];
const DESCRICOES_PDF_UNICO = ORDEM_PDF_UNICO.flat().map(normalizarDescricao);

// Assinatura coletada no app (anexo "Assinatura do Cliente") — baixa pelo mesmo cache dos anexos.
function AssinaturaCliente({ anexo }) {
    const [url, setUrl] = useState(null);
    const [erro, setErro] = useState(false);

    useEffect(() => {
        let ativo = true;
        setUrl(null);
        setErro(false);
        obterArquivoAnexo(anexo)
            .then(({ url: u }) => ativo && setUrl(u))
            .catch(() => ativo && setErro(true));
        return () => { ativo = false; };
    }, [anexo]);

    return (
        <div className="flex flex-col gap-1">
            <div className="h-32 w-full max-w-sm rounded-lg border border-border bg-white p-2 flex items-center justify-center">
                {url && <img src={url} alt="Assinatura do cliente" className="max-h-full max-w-full object-contain" />}
                {!url && !erro && <span className="text-xs text-muted-foreground">Carregando assinatura...</span>}
                {erro && <span className="text-xs text-destructive">Não foi possível carregar a assinatura.</span>}
            </div>
            <span className="text-xs text-muted-foreground">
                Assinatura do cliente{anexo.dataHora ? ` em ${formatarDataHora(anexo.dataHora)}` : ""}
            </span>
        </div>
    );
}

// Dados acumulados do processo — sempre visível, independentemente da etapa atual. `anexos`: os da solicitação
// (vindos da página de detalhe), usados no PDF único dos documentos.
export default function DetalheRenovacaoGdm({ detalheProcesso, anexos = [], solicitacao }) {
    const [pdfUnicoAberto, setPdfUnicoAberto] = useState(false);
    if (!detalheProcesso) return null;
    const d = detalheProcesso;
    const resultado = d.renovado === true ? "Sim — GDM renovada" : d.renovado === false ? "Não renovada" : null;
    const documentosParaPdf = anexos.filter(a => DESCRICOES_PDF_UNICO.includes(normalizarDescricao(a.descricao))).length;
    const prefixoPdf = d.clienteCodigo || solicitacao?.numero || "renovacao";

    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-6 flex flex-col gap-4">
            <div>
                <h2 className="font-display text-base font-semibold text-foreground mb-4">Cliente</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <Campo label="Código" valor={d.clienteCodigo} />
                    <div className="col-span-2">
                        <Campo label="Razão Social" valor={d.clienteRazaoSocial} />
                    </div>
                    <Campo label="Nome Fantasia" valor={d.clienteFantasia} />
                </div>
                {d.documentosObrigatorios?.length > 0 && (
                    <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                        <i className="fa fa-paperclip mr-1" />
                        Anexos obrigatórios para enviar: <strong>{d.documentosObrigatorios.join(", ")}</strong>.
                        Use exatamente essas descrições nos anexos.
                    </p>
                )}
                {d.documentosNaoEnviados?.length > 0 && (
                    <div className="mt-3 text-sm flex flex-col gap-1">
                        <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Documentos não enviados</h3>
                        {d.documentosNaoEnviados.map(n => (
                            <p key={n.documento}><strong>{n.documento}:</strong> {n.motivo}</p>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
                <div className="text-sm">
                    <span className="font-semibold text-foreground">PDF único dos documentos</span>
                    <span className="block text-xs text-muted-foreground">
                        {documentosParaPdf > 0
                            ? `Junta Contrato → Documento do Responsável → Comprovante de Endereço (${documentosParaPdf} anexo(s)). Dá para girar e recortar as imagens antes de baixar.`
                            : "Nenhum contrato, documento ou comprovante anexado ainda."}
                    </span>
                </div>
                <Button variant="outline" label="Gerar PDF único" iconLeft="fa fa-file-pdf" disabled={documentosParaPdf === 0}
                    onClick={() => setPdfUnicoAberto(true)} />
            </div>
            <PdfUnicoModal
                visible={pdfUnicoAberto}
                onHide={() => setPdfUnicoAberto(false)}
                anexos={anexos}
                ordem={ORDEM_PDF_UNICO}
                nomeArquivo={`${prefixoPdf} - Renovação GDM.pdf`}
            />

            <div>
                <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Assinatura do cliente</h3>
                {d.assinatura
                    ? <AssinaturaCliente anexo={d.assinatura} />
                    : <p className="text-sm text-muted-foreground">Não coletada (opcional).</p>}
            </div>

            <div>
                <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">GDM a ser renovada</h3>
                {d.gdm
                    ? <GdmDadosCampos gdm={d.gdm} />
                    : <p className="text-sm text-muted-foreground">O cliente não tinha GDM na base no momento da solicitação.</p>}
            </div>

            {(d.justificativaAprovacao || resultado) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {d.justificativaAprovacao && (
                        <div>
                            <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Justificativa da Aprovação</h3>
                            <p className="text-sm text-foreground whitespace-pre-wrap">{d.justificativaAprovacao}</p>
                        </div>
                    )}
                    {resultado && (
                        <div>
                            <h3 className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Confirmação de Renovação</h3>
                            <p className={`text-sm font-semibold ${d.renovado ? "text-green-700" : "text-destructive"}`}>{resultado}</p>
                            {d.justificativaNaoRenovacao && (
                                <p className="text-sm text-foreground whitespace-pre-wrap mt-1">{d.justificativaNaoRenovacao}</p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
